import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import type { PublishError } from '../../../types/metaAdDraft';
import { publishErrorGuidance, publishErrorTitle } from '../publishSummary';

// Stage 7A D3 TASK 5 — publish failure. Category-titled, with "what to do"
// guidance and the raw technical message collapsed by default. Retry re-fires the
// mutation with the current version (no re-confirmation — the AM already
// confirmed). The draft stays editable (D2.2 reverts publishing→draft on error).

type Props = {
  open: boolean;
  publishError: PublishError | null;
  accountId?: string | null;
  retrying: boolean;
  onClose: () => void;
  onRetry: () => void;
};

export function PublishErrorModal({ open, publishError, accountId, retrying, onClose, onRetry }: Props) {
  const [showDetails, setShowDetails] = useState(false);
  const category = publishError?.category ?? 'unknown';

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o && !retrying) onClose();
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{publishErrorTitle(category)}</DialogTitle>
        </DialogHeader>

        <p className="text-sm text-slate-700">{publishErrorGuidance(category)}</p>

        {category === 'verified_advertiser_required' && accountId && (
          <a
            href={`/accounts/${accountId}?tab=connections`}
            target="_blank"
            rel="noreferrer"
            className="text-sm font-medium text-kyck-600 underline"
          >
            Open the account's Connections tab
          </a>
        )}

        {publishError?.message && (
          <div className="text-sm">
            <button
              type="button"
              onClick={() => setShowDetails((s) => !s)}
              className="text-xs font-medium text-slate-500 underline hover:text-slate-700"
              aria-expanded={showDetails}
            >
              {showDetails ? 'Hide details' : 'Show details'}
            </button>
            {showDetails && (
              <pre className="mt-2 max-h-40 overflow-auto whitespace-pre-wrap rounded-md border border-slate-200 bg-slate-50 p-2 text-xs text-slate-600">
                {publishError.message}
              </pre>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={retrying}>
            Close
          </Button>
          <Button onClick={onRetry} disabled={retrying}>
            {retrying ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Retrying…
              </>
            ) : (
              'Retry publish'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
